/**
 * Reine Overflow-Rechnung des FilterStrip (Spec:
 * docs/superpowers/specs/2026-07-25-filterstrip-overflow-design.md §3).
 * Kein DOM-Zugriff — Eingabe sind gemessene Breiten, Ausgabe ist der Layout-Zustand.
 */

/** Default-Mindestbreite der Suche. Der Strip überschreibt sie mit dem Wert
 *  aus dem CSS — im Mobile-Dock ist die Suche breiter geschützt. */
export const SEARCH_MIN_WIDTH = 150;
export const SEARCH_BASE_WIDTH = 280;
/** Flex-Gap zwischen Gruppen-Label und Items innerhalb einer Group (--space-sm). */
export const LABEL_GAP = 8;
export const DEFAULT_HYSTERESIS = 24;
/** Puffer für Mess-Unschärfen (Group-Separator/Padding-Varianz erste Gruppe). */
export const DEFAULT_SAFETY = 12;

export function computeOverflowLayout({
  containerWidth,
  gap,
  leftItems = [],
  rightItems = [],
  fixedWidth = 0,
  searchMinWidth = SEARCH_MIN_WIDTH,
  moreFullWidth,
  moreCompactWidth,
  hasPermanentExtras = false,
  previousEvictedIds = [],
  hysteresis = DEFAULT_HYSTERESIS,
  safety = DEFAULT_SAFETY,
}) {
  const solve = (width) => {
    const left = [...leftItems];
    const right = [...rightItems];
    const evictedIds = [];
    let labelsHidden = false;

    const fits = () => {
      const visible = [...left, ...right];
      const showMore = hasPermanentExtras || evictedIds.length > 0;
      const moreW = showMore ? (labelsHidden ? moreCompactWidth : moreFullWidth) : 0;
      const itemsW = visible.reduce(
        (sum, it) => sum + it.width - (labelsHidden && it.labelWidth ? it.labelWidth + LABEL_GAP : 0),
        0
      );
      const parts =
        1 + visible.length + (showMore ? 1 : 0) + (fixedWidth > 0 ? 1 : 0);
      const gaps = gap * Math.max(0, parts - 1);
      return searchMinWidth + itemsW + fixedWidth + moreW + gaps <= width - safety;
    };

    while (!fits()) {
      const takeRight = right.length > left.length; // Balance: vollere Seite, Gleichstand links
      const victimSide = takeRight ? right : left;
      if (victimSide.length === 0) {
        // nichts Ausweichbares mehr — letzter Rettungsversuch, dann ist Minimum erreicht
        if (!labelsHidden) { labelsHidden = true; continue; }
        break; // Suche schrumpft real weiter per CSS (min-width), Zeile bricht nie
      }
      if (takeRight && !labelsHidden) {
        // Beschriftungs-Stufe: Rettungsversuch VOR dem ersten Rechts-Opfer
        labelsHidden = true;
        continue;
      }
      const victim = takeRight ? right.shift() : left.pop(); // innen zuerst
      evictedIds.push(victim.id);
    }

    return {
      evictedIds,
      labelsHidden,
      moreCompact: labelsHidden,
      showMore: hasPermanentExtras || evictedIds.length > 0,
    };
  };

  const now = solve(containerWidth);

  // Hysterese: Elemente kehren nur zurück, wenn selbst eine um `hysteresis`
  // schmalere Zeile sie nicht wieder ausweichen ließe (kein Flattern an der Kippkante).
  const existing = new Set([...leftItems, ...rightItems].map((it) => it.id));
  const prev = previousEvictedIds.filter((id) => existing.has(id));
  const wouldReturn = prev.some((id) => !now.evictedIds.includes(id));
  if (wouldReturn) {
    const shrunk = solve(containerWidth - hysteresis);
    const blocked = prev.some(
      (id) => !now.evictedIds.includes(id) && shrunk.evictedIds.includes(id)
    );
    if (blocked) return shrunk;
  }
  return now;
}
